import { Col, Hr, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { clearCachedReview, getCachedReview, isReviewMatchingCache } from "common/helpers/review-cache";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useProductFeedback } from "hooks/useProductFeedback";
import { CatalogicalDomain, UserPermissions } from "lib/services";
import { FeedbackModelRecord } from "lib/services/catalogical-models";
import React, { FC, useEffect, useState } from "react";

import { ProductReviewCard } from "./ProductReviewCard";

export interface ProductReviewsListProps {
  domain: CatalogicalDomain;
  taxonomyId: TaxonomyIdItem;
  refreshTrigger?: number; //triggers reviews refresh
}

export const ProductReviewsList: FC<ProductReviewsListProps> = (props) => {
  const {
    productFeedback: feedback,
    userFeedback,
    error,
    isLoading,
    doGetReviews,
    doGetUserReviews,
  } = useProductFeedback({ domain: props.domain });
  const username = UserPermissions.current.username ?? "";

  useEffect(() => {
    doGetReviews({ taxonomyId: props.taxonomyId });
    doGetUserReviews({ taxonomyId: props.taxonomyId, username: username });
  }, [props.refreshTrigger]);

  // Read cached review once and store in state; refresh when trigger changes
  const [cachedReview, setCachedReview] = useState<FeedbackModelRecord | null>(() =>
    props.taxonomyId ? getCachedReview(props.taxonomyId.toString()) : null
  );

  useEffect(() => {
    setCachedReview(props.taxonomyId ? getCachedReview(props.taxonomyId.toString()) : null);
  }, [props.refreshTrigger]);

  // Clear cache once backend has caught up (uses a deep match)
  useEffect(() => {
    if (cachedReview && feedback.some((f) => isReviewMatchingCache(cachedReview, f))) {
      clearCachedReview(props.taxonomyId.toString());
      setCachedReview(null);
    }
  }, [feedback, cachedReview]);

  const isValidReview = (feedback: FeedbackModelRecord) => {
    return (
      feedback &&
      feedback.title &&
      feedback.title?.length > 0 &&
      feedback.categories &&
      feedback.categories.length > 0 &&
      feedback.content &&
      feedback.content?.length > 0
    );
  };

  const generateReviewCards = () => {
    const reviewCards: JSX.Element[] = [];
    const hasCachedReview = cachedReview && !feedback.some((f) => isReviewMatchingCache(cachedReview, f));

    // 1. User's review first (cached takes priority over fetched)
    if (hasCachedReview && isValidReview(cachedReview)) {
      reviewCards.push(<Hr key="hr-cached" />);
      reviewCards.push(
        <ProductReviewCard
          key="review-cached"
          content={cachedReview.content!}
          jobFamily={cachedReview.jobFamily!}
          title={cachedReview.title!}
          categories={cachedReview.categories!}
          publishedDate={cachedReview.sortKey}
          score={cachedReview.rating}
          isSelf={true}
          dataTestId="cached-product-review-card"
        />
      );
    } else if (userFeedback.length > 0 && isValidReview(userFeedback[0])) {
      const userReview = userFeedback[0];
      reviewCards.push(<Hr key={`hr-${userReview.feedbackId}`} />);
      reviewCards.push(
        <ProductReviewCard
          key={`review-${userReview.feedbackId}`}
          content={userReview.content!}
          jobFamily={userReview.jobFamily!}
          title={userReview.title!}
          categories={userReview.categories!}
          publishedDate={userReview.sortKey}
          score={userReview.rating}
          isSelf={true}
        />
      );
    }

    // 2. Remaining reviews, skip user's to avoid duplicate
    const userFeedbackIds = new Set(userFeedback.map((f) => f.feedbackId));
    // TODO: Remove client-side sorting once Catalogical supports sortBy on feedback queries
    const sortedFeedback = [...feedback].sort((a, b) => new Date(b.sortKey).getTime() - new Date(a.sortKey).getTime());
    sortedFeedback.forEach((record) => {
      if ((hasCachedReview || userFeedback.length > 0) && record.username === username) return;
      if (userFeedbackIds.has(record.feedbackId)) return;
      if (hasCachedReview && record.feedbackId === cachedReview.feedbackId) return;
      if (!isValidReview(record)) return;
      reviewCards.push(<Hr key={`hr-${record.feedbackId}`} />);
      reviewCards.push(
        <ProductReviewCard
          key={`review-${record.feedbackId}`}
          content={record.content!}
          jobFamily={record.jobFamily!}
          title={record.title!}
          categories={record.categories!}
          publishedDate={record.sortKey}
          score={record.rating}
          isSelf={false}
        />
      );
    });

    if (reviewCards.length < 1) reviewCards.push(NoReviews);
    return reviewCards;
  };

  const renderLoadingComponent = () => {
    return (
      <Col alignItems="center" justifyContent="center" minHeight="400px">
        <Spinner size={SpinnerSize.Small} />
        <Text fontSize={"S100"}>{getString("productReviews.loading")}</Text>
      </Col>
    );
  };

  const NoReviews = (
    <View key="no-reviews" dataTestId="product-reviews-no-reviews">
      <Hr />
      <Text margin={"16px"}>{getString("productReviews.noReviews")}</Text>
    </View>
  );

  return (
    <Col dataTestId="product-reviews-list" height={"100%"} flex="1">
      {isLoading ? renderLoadingComponent() : generateReviewCards()}
    </Col>
  );
};
